import React, { useState, useRef, useEffect } from 'react';
import {
    Card,
    Button,
    Input,
    Typography,
    Tag,
    Space,
    Spin,
    Alert,
    Badge,
    Row,
    Col,
    Modal,
    message,
} from 'antd';
import {
    SendOutlined,
    CloudUploadOutlined,
    FileTextOutlined,
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    LoadingOutlined,
    UndoOutlined,
    RollbackOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import {
    useEditorChatMutation,
    useEditorDiscardMutation,
    useEditorRevertMutation,
    useEditorPublishMutation,
    useGetEditorStatusQuery,
} from '../../services/nestjs-base-api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface ChatMessage {
    role: 'user' | 'assistant' | 'error';
    content: string;
    editsApplied?: string[];
    summary?: string;
}

export const CorporateSitePage: React.FC = () => {
    const { user } = useAuth();
    const intl = useIntl();
    const jwtToken = user?.jwtToken || '';

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [conversationHistory, setConversationHistory] = useState<
        Array<{ role: string; content: string }>
    >([]);
    const [hasEdits, setHasEdits] = useState(false);
    const [publishModalOpen, setPublishModalOpen] = useState(false);
    const [commitMessage, setCommitMessage] = useState('');

    const messagesEndRef = useRef<HTMLDivElement>(null);

    const [editorChat, { isLoading: isChatLoading }] = useEditorChatMutation();
    const [editorDiscard, { isLoading: isDiscarding }] = useEditorDiscardMutation();
    const [editorRevert, { isLoading: isReverting }] = useEditorRevertMutation();
    const [editorPublish, { isLoading: isPublishing }] = useEditorPublishMutation();
    const {
        data: gitStatus,
        isLoading: isStatusLoading,
        isError: isStatusError,
        error: statusError,
        refetch: refetchStatus,
    } = useGetEditorStatusQuery({ jwtToken }, { skip: !jwtToken });

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (): Promise<void> => {
        if (!inputValue.trim() || isChatLoading) return;

        const userMessage = inputValue.trim();
        setInputValue('');
        setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);

        try {
            const result = await editorChat({
                jwtToken,
                payload: {
                    message: userMessage,
                    conversationHistory,
                },
            }).unwrap();

            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content: result.assistantMessage,
                    editsApplied: result.editsApplied,
                    summary: result.summary,
                },
            ]);
            setConversationHistory(result.conversationHistory);

            if (result.editsApplied.length > 0) {
                setHasEdits(true);
            }
        } catch {
            setMessages((prev) => [
                ...prev,
                {
                    role: 'error',
                    content: intl.formatMessage({
                        id: 'admin.corporateSite.chatError',
                    }),
                },
            ]);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const openPublishModal = (): void => {
        const editSummaries = messages
            .filter((m) => m.role === 'assistant' && m.summary)
            .map((m) => m.summary as string);
        const generated = editSummaries.length > 0
            ? editSummaries.join('; ')
            : 'Content update via AI editor';
        setCommitMessage(generated);
        setPublishModalOpen(true);
    };

    const handlePublish = async (): Promise<void> => {
        try {
            const result = await editorPublish({
                jwtToken,
                payload: { summary: commitMessage },
            }).unwrap();

            setPublishModalOpen(false);

            if (result.commitSha) {
                message.success(
                    `${intl.formatMessage({ id: 'admin.corporateSite.publishSuccess' })} (${result.commitSha})`,
                );
                setHasEdits(false);
                setMessages((prev) =>
                    prev.map((m) => ({ ...m, summary: undefined })),
                );
            } else {
                message.info(result.message);
            }
        } catch {
            message.error(
                intl.formatMessage({ id: 'admin.corporateSite.publishError' }),
            );
        }
    };

    const handleDiscard = (): void => {
        Modal.confirm({
            title: intl.formatMessage({ id: 'admin.corporateSite.discard' }),
            content: intl.formatMessage({ id: 'admin.corporateSite.discardConfirm' }),
            icon: <ExclamationCircleOutlined />,
            okType: 'danger',
            onOk: async () => {
                try {
                    await editorDiscard({ jwtToken }).unwrap();
                    message.success(intl.formatMessage({ id: 'admin.corporateSite.discardSuccess' }));
                    setHasEdits(false);
                    setMessages((prev) => prev.map((m) => ({ ...m, summary: undefined })));
                } catch {
                    message.error(intl.formatMessage({ id: 'admin.corporateSite.discardError' }));
                }
            },
        });
    };

    const handleRevert = (): void => {
        Modal.confirm({
            title: intl.formatMessage({ id: 'admin.corporateSite.revert' }),
            content: intl.formatMessage({ id: 'admin.corporateSite.revertConfirm' }),
            icon: <ExclamationCircleOutlined />,
            okType: 'danger',
            onOk: async () => {
                try {
                    const result = await editorRevert({ jwtToken }).unwrap();
                    message.success(
                        `${intl.formatMessage({ id: 'admin.corporateSite.revertSuccess' })} (${result.commitSha})`,
                    );
                    setHasEdits(false);
                    setMessages((prev) => prev.map((m) => ({ ...m, summary: undefined })));
                } catch {
                    message.error(intl.formatMessage({ id: 'admin.corporateSite.revertError' }));
                }
            },
        });
    };

    const pendingChanges =
        (gitStatus?.modified?.length || 0) +
        (gitStatus?.added?.length || 0) +
        (gitStatus?.deleted?.length || 0);

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}>
                <FormattedMessage id="admin.corporateSite.title" />
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                <FormattedMessage id="admin.corporateSite.subtitle" />
            </Text>

            <Row gutter={24}>
                {/* Chat Section */}
                <Col xs={24} lg={16}>
                    <Card
                        style={{ height: 'calc(100vh - 240px)', display: 'flex', flexDirection: 'column' }}
                        styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' } }}
                    >
                        {/* Messages */}
                        <div
                            style={{
                                flex: 1,
                                overflowY: 'auto',
                                padding: 16,
                            }}
                        >
                            {messages.length === 0 && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        color: '#999',
                                    }}
                                >
                                    <Text type="secondary">
                                        <FormattedMessage id="admin.corporateSite.chatPlaceholder" />
                                    </Text>
                                </div>
                            )}

                            {messages.map((msg, index) => (
                                <div
                                    key={index}
                                    style={{
                                        display: 'flex',
                                        justifyContent:
                                            msg.role === 'user' ? 'flex-end' : 'flex-start',
                                        marginBottom: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            maxWidth: '80%',
                                            padding: '10px 16px',
                                            borderRadius: 12,
                                            backgroundColor:
                                                msg.role === 'user'
                                                    ? '#F59E0B'
                                                    : msg.role === 'error'
                                                      ? '#fff2f0'
                                                      : '#f5f5f5',
                                            color: msg.role === 'user' ? '#fff' : '#333',
                                            border:
                                                msg.role === 'error'
                                                    ? '1px solid #ffccc7'
                                                    : 'none',
                                        }}
                                    >
                                        <Paragraph
                                            style={{
                                                margin: 0,
                                                color: 'inherit',
                                                whiteSpace: 'pre-wrap',
                                            }}
                                        >
                                            {msg.content}
                                        </Paragraph>
                                        {msg.editsApplied && msg.editsApplied.length > 0 && (
                                            <div style={{ marginTop: 8 }}>
                                                <Tag color="blue" icon={<FileTextOutlined />}>
                                                    <FormattedMessage
                                                        id="admin.corporateSite.filesChanged"
                                                        values={{ count: msg.editsApplied.length }}
                                                    />
                                                </Tag>
                                                <div style={{ marginTop: 4 }}>
                                                    {msg.editsApplied.map((file) => (
                                                        <Text
                                                            key={file}
                                                            code
                                                            style={{
                                                                fontSize: 11,
                                                                display: 'block',
                                                                color: '#666',
                                                            }}
                                                        >
                                                            {file}
                                                        </Text>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {isChatLoading && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        marginBottom: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            padding: '10px 16px',
                                            borderRadius: 12,
                                            backgroundColor: '#f5f5f5',
                                        }}
                                    >
                                        <Spin size="small" />
                                    </div>
                                </div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input */}
                        <div
                            style={{
                                padding: 16,
                                borderTop: '1px solid #f0f0f0',
                            }}
                        >
                            <Space.Compact style={{ width: '100%' }}>
                                <TextArea
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={intl.formatMessage({
                                        id: 'admin.corporateSite.chatPlaceholder',
                                    })}
                                    autoSize={{ minRows: 1, maxRows: 4 }}
                                    disabled={isChatLoading}
                                    style={{ borderRadius: '8px 0 0 8px' }}
                                />
                                <Button
                                    type="primary"
                                    icon={<SendOutlined />}
                                    onClick={handleSend}
                                    loading={isChatLoading}
                                    disabled={!inputValue.trim()}
                                    style={{ height: 'auto', borderRadius: '0 8px 8px 0' }}
                                >
                                    <FormattedMessage id="admin.corporateSite.send" />
                                </Button>
                            </Space.Compact>
                        </div>
                    </Card>
                </Col>

                {/* Status & Publish Section */}
                <Col xs={24} lg={8}>
                    {/* Repository Connection Status */}
                    <Card size="small" style={{ marginBottom: 16 }}>
                        <Space>
                            {isStatusLoading ? (
                                <>
                                    <LoadingOutlined style={{ color: '#faad14' }} />
                                    <Text type="secondary">
                                        <FormattedMessage id="admin.corporateSite.repoConnecting" />
                                    </Text>
                                </>
                            ) : isStatusError ? (
                                <>
                                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                                    <Text type="danger">
                                        <FormattedMessage id="admin.corporateSite.repoError" />
                                    </Text>
                                </>
                            ) : gitStatus ? (
                                <>
                                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                    <Text type="success">
                                        <FormattedMessage id="admin.corporateSite.repoConnected" />
                                    </Text>
                                </>
                            ) : null}
                        </Space>
                        {isStatusError && (
                            <Alert
                                type="error"
                                showIcon={false}
                                style={{ marginTop: 8 }}
                                message={
                                    <Text style={{ fontSize: 12 }}>
                                        {(statusError as any)?.data?.message || 'Could not connect to the repository. Check BITBUCKET_REPO_URL in .env'}
                                    </Text>
                                }
                            />
                        )}
                    </Card>

                    <Card
                        title={
                            <FormattedMessage id="admin.corporateSite.gitStatus" />
                        }
                        style={{ marginBottom: 16 }}
                    >
                        {isStatusLoading ? (
                            <Spin />
                        ) : gitStatus ? (
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div>
                                    <Space>
                                        <Badge
                                            count={gitStatus.modified.length}
                                            showZero
                                            color="#faad14"
                                        />
                                        <Text>
                                            <EditOutlined style={{ marginRight: 4 }} />
                                            <FormattedMessage id="admin.corporateSite.modified" />
                                        </Text>
                                    </Space>
                                </div>
                                <div>
                                    <Space>
                                        <Badge
                                            count={gitStatus.added.length}
                                            showZero
                                            color="#52c41a"
                                        />
                                        <Text>
                                            <PlusOutlined style={{ marginRight: 4 }} />
                                            <FormattedMessage id="admin.corporateSite.added" />
                                        </Text>
                                    </Space>
                                </div>
                                <div>
                                    <Space>
                                        <Badge
                                            count={gitStatus.deleted.length}
                                            showZero
                                            color="#ff4d4f"
                                        />
                                        <Text>
                                            <DeleteOutlined style={{ marginRight: 4 }} />
                                            <FormattedMessage id="admin.corporateSite.deleted" />
                                        </Text>
                                    </Space>
                                </div>
                                <div style={{ marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        <FormattedMessage id="admin.corporateSite.lastCommit" />
                                    </Text>
                                    <br />
                                    <Text code style={{ fontSize: 11 }}>
                                        {gitStatus.lastCommit}
                                    </Text>
                                </div>
                            </Space>
                        ) : (
                            <Alert
                                type="info"
                                message={
                                    <FormattedMessage id="admin.corporateSite.noPendingChanges" />
                                }
                            />
                        )}
                    </Card>

                    <Button
                        type="primary"
                        icon={<CloudUploadOutlined />}
                        size="large"
                        block
                        onClick={openPublishModal}
                        disabled={!hasEdits && pendingChanges === 0}
                    >
                        <FormattedMessage id="admin.corporateSite.publish" />
                    </Button>

                    {pendingChanges === 0 && !hasEdits && (
                        <Text
                            type="secondary"
                            style={{
                                display: 'block',
                                textAlign: 'center',
                                marginTop: 8,
                                fontSize: 12,
                            }}
                        >
                            <FormattedMessage id="admin.corporateSite.noPendingChanges" />
                        </Text>
                    )}

                    <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                        <Button
                            icon={<UndoOutlined />}
                            block
                            onClick={handleDiscard}
                            loading={isDiscarding}
                            disabled={pendingChanges === 0 && !hasEdits}
                        >
                            <FormattedMessage id="admin.corporateSite.discard" />
                        </Button>
                        <Button
                            danger
                            icon={<RollbackOutlined />}
                            block
                            onClick={handleRevert}
                            loading={isReverting}
                        >
                            <FormattedMessage id="admin.corporateSite.revert" />
                        </Button>
                    </div>
                </Col>
            </Row>

            <Modal
                title={<FormattedMessage id="admin.corporateSite.publishModalTitle" />}
                open={publishModalOpen}
                onCancel={() => setPublishModalOpen(false)}
                onOk={handlePublish}
                confirmLoading={isPublishing}
                okText={intl.formatMessage({ id: 'admin.corporateSite.publish' })}
            >
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    <FormattedMessage id="admin.corporateSite.commitMessageLabel" />
                </Text>
                <TextArea
                    value={commitMessage}
                    onChange={(e) => setCommitMessage(e.target.value)}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                />
                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                    <FormattedMessage id="admin.corporateSite.commitMessageHint" />
                </Text>
            </Modal>
        </div>
    );
};
